import { Autocomplete, Box, TextField } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { useGetCurrentOrganization } from '../../../services/react-query/organization';
import { Address } from '../../../types/Address';
import { CountriesList, StatesByCountry } from '../../../utility/countryData';
import { errorPopulator } from '../../../utility/inputUtils';

export const REQUIRED_FIELDS: (keyof Address)[] = ['address_1', 'city', 'country', 'division', 'type_value'];
export const BLANK_ADDRESS: Address = {
  name: '',
  address_1: '',
  address_2: '',
  city: '',
  postal_code: '',
  country: '',
  division: '',
  uuid: '',
  type_value: '',
  type_uuid: '',
};

interface AddressFormProps {
  address: Address;
  setAddress: Dispatch<SetStateAction<Address>>;
  isDisabled?: boolean;
}

const AddressForm: React.FC<AddressFormProps> = ({ address, setAddress, isDisabled = false }) => {
  const { data: organization } = useGetCurrentOrganization({});
  const organizationCountry = organization?.settings?.country || '';

  // Set default country from org settings if not already set
  useEffect(() => {
    if (!address.country && organizationCountry) {
      setAddress((prev) => ({
        ...prev,
        country: organizationCountry,
      }));
    }
  }, [organizationCountry, address.country, setAddress]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setAddress((prev) => ({ ...prev, [id]: value }));
  };

  const isRequired = (field: keyof Address) => REQUIRED_FIELDS.includes(field);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Name"
        id="name"
        value={address.name || ''}
        onChange={handleChange}
        disabled={isDisabled}
      />

      <TextField
        label="Address Line 1"
        id="address_1"
        value={address.address_1}
        onChange={handleChange}
        required={isRequired('address_1')}
        disabled={isDisabled}
      />

      <TextField label="Address Line 2" id="address_2" value={address.address_2} onChange={handleChange} disabled={isDisabled} />

      <TextField label="City" id="city" value={address.city} onChange={handleChange} required={isRequired('city')} disabled={isDisabled} />

      <Autocomplete<string>
        disabled={isDisabled}
        options={CountriesList}
        value={address.country}
        onChange={(_, newCountry) => {
          setAddress((prev) => ({
            ...prev,
            country: newCountry || '',
            division: '', // Clear division if country changes
          }));
        }}
        renderInput={(params) => (
          <TextField {...params} {...errorPopulator(address, 'country')} label="Country" required={isRequired('country')} />
        )}
      />

      <Autocomplete<string>
        disabled={isDisabled}
        options={StatesByCountry[address.country] || []}
        value={address.division || ''}
        onChange={(_, newDivision) => {
          setAddress((prev) => ({ ...prev, division: newDivision || '' }));
        }}
        renderInput={(params) => (
          <TextField {...params} {...errorPopulator(address, 'division')} label="Province / State" required={isRequired('division')} />
        )}
      />

      <TextField label="Postal Code" id="postal_code" value={address.postal_code} onChange={handleChange} disabled={isDisabled} />
    </Box>
  );
};

export default AddressForm;
